import { create } from 'zustand';

import { db } from '@/services/storage/db';
import { LogEntryService } from '@/services/log-entry-service';
import { WaypointService } from '@/services/waypoint-service';
import { getCelestialData } from '@/services/celestial-service';
import { getWeatherForProperty, computeConditionsDisplay, windDegToCardinal, hpaToInHg } from '@/services/weather-service';
import { computeConditionsQuality } from '@/services/conditions-quality-service';
import type { LogEntry, CreateLogEntryInput, UpdateLogEntryInput } from '@/types/log-entry';
import type { LogEntryConditionsQuality } from '@/types/conditions';

const logEntryService = new LogEntryService(db);
const waypointService = new WaypointService(db);

interface LogEntryStore {
  entries: LogEntry[];
  recentEntries: LogEntry[];
  /** Monotonic mutation counter — bumped on every create/update/delete. DashboardPage
   *  subscribes to this to invalidate its getDashboardData() snapshot when log entries
   *  mutate out-of-band, mirroring the O-1 refreshKey-invalidation pattern at
   *  DashboardPage.tsx:179-190 (defect: dashboardpage-stale-snapshot-body-consumers.md). */
  revision: number;
  isLoading: boolean;
  error: string | null;
  loadEntriesByWaypoint: (waypointId: string) => Promise<void>;
  loadEntriesByProperty: (propertyId: string) => Promise<void>;
  loadRecentEntries: (limit?: number) => Promise<void>;
  createEntry: (data: CreateLogEntryInput) => Promise<LogEntry>;
  updateEntry: (id: string, data: UpdateLogEntryInput) => Promise<LogEntry>;
  deleteEntry: (id: string) => Promise<void>;
}

export const useLogEntryStore = create<LogEntryStore>((set) => ({
  entries: [],
  recentEntries: [],
  revision: 0,
  isLoading: false,
  error: null,

  loadEntriesByWaypoint: async (waypointId) => {
    set({ isLoading: true, error: null });
    try {
      const entries = await logEntryService.getEntries(waypointId);
      set({ entries, isLoading: false });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load log entries';
      console.error('LogEntryStore.loadEntriesByWaypoint:', err);
      set({ error: message, isLoading: false });
    }
  },

  loadEntriesByProperty: async (propertyId) => {
    set({ isLoading: true, error: null });
    try {
      const entries = await logEntryService.getEntriesByProperty(propertyId);
      set({ entries, isLoading: false });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load property log entries';
      console.error('LogEntryStore.loadEntriesByProperty:', err);
      set({ error: message, isLoading: false });
    }
  },

  loadRecentEntries: async (limit) => {
    set({ isLoading: true, error: null });
    try {
      const recentEntries = await logEntryService.getRecentEntries(limit);
      set({ recentEntries, isLoading: false });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load recent log entries';
      console.error('LogEntryStore.loadRecentEntries:', err);
      set({ error: message, isLoading: false });
    }
  },

  createEntry: async (data) => {
    try {
      // Auto-populate celestial data from the entry's location coordinates and date
      let celestialData: Partial<CreateLogEntryInput> = {};
      const location = await waypointService.getWaypoint(data.waypointId).catch(() => null);
      if (location) {
        const entryDate = data.date ? new Date(data.date) : new Date();
        const celestial = getCelestialData(entryDate, location.latitude, location.longitude);
        celestialData = {
          moonPhase: celestial.moon.phase,
          moonIllumination: celestial.moon.illumination,
          sunriseTime: celestial.sun.sunrise.toISOString(),
          sunsetTime: celestial.sun.sunset.toISOString(),
          civilDawnTime: celestial.sun.civilDawn.toISOString(),
          civilDuskTime: celestial.sun.civilDusk.toISOString(),
        };
      }

      // Auto-populate weather data and conditions quality if the location has a propertyId
      let weatherData: Partial<CreateLogEntryInput> = {};
      let qualitySnapshot: LogEntryConditionsQuality | null = null;

      if (location?.propertyId) {
        try {
          const weather = await getWeatherForProperty(location.propertyId, location.latitude, location.longitude);
          if (weather) {
            const display = computeConditionsDisplay(weather);
            weatherData = {
              weatherTemp: weather.current.temp,
              weatherFeelsLike: weather.current.feelsLike,
              weatherCondition: weather.current.condition,
              weatherHumidity: weather.current.humidity,
              weatherIcon: weather.current.icon,
              windSpeed: weather.current.windSpeed,
              windDirection: windDegToCardinal(weather.current.windDeg),
              barometricPressure: hpaToInHg(weather.current.pressure),
              barometricTrend: display.pressureTrend,
            };

            // Snapshot conditions quality at entry creation time
            const entryDate = data.date ? new Date(data.date) : new Date();
            const celestial = getCelestialData(entryDate, location.latitude, location.longitude);
            const qualityResult = computeConditionsQuality(display, celestial, entryDate);
            if (qualityResult) {
              qualitySnapshot = {
                score: qualityResult.score,
                level: qualityResult.level,
                variables: qualityResult.variables,
              };
            }
          }
        } catch (err) {
          // Weather auto-population is best-effort — offline entries get celestial only
          console.warn('[log-entry-store] Weather auto-population failed (offline?):', err);
        }
      }

      const entry = await logEntryService.createEntry({
        ...data,
        ...celestialData,
        ...weatherData,
        conditionsQuality: qualitySnapshot,
      });
      set((state) => ({
        entries: [entry, ...state.entries].sort((a, b) => b.date.localeCompare(a.date)),
        revision: state.revision + 1,
      }));
      return entry;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create log entry';
      console.error('LogEntryStore.createEntry:', err);
      set({ error: message });
      throw err;
    }
  },

  updateEntry: async (id, data) => {
    try {
      const updated = await logEntryService.updateEntry(id, data);
      set((state) => ({
        entries: state.entries.map((entry) => (entry.id === id ? updated : entry)),
        revision: state.revision + 1,
      }));
      return updated;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update log entry';
      console.error('LogEntryStore.updateEntry:', err);
      set({ error: message });
      throw err;
    }
  },

  deleteEntry: async (id) => {
    try {
      // Cascade: remove associated photo blobs before deleting the entry
      const { deletePhotosForEntry } = await import('@/services/photo-service');
      await deletePhotosForEntry(id).catch((err) => {
        // Best-effort cleanup — log but don't block entry deletion
        console.warn('[log-entry-store] Photo cleanup failed:', err);
      });
      await logEntryService.deleteEntry(id);
      set((state) => ({
        entries: state.entries.filter((entry) => entry.id !== id),
        revision: state.revision + 1,
      }));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete log entry';
      console.error('LogEntryStore.deleteEntry:', err);
      set({ error: message });
      throw err;
    }
  },
}));
